import { useEffect, useRef, useState, useMemo } from "react";
import { toPng } from "html-to-image";
import { Area, AreaChart, CartesianGrid, XAxis, YAxis } from "recharts";
import { useAtom } from "jotai";
import { Document, Page, View, Image, PDFViewer, Text } from "@react-pdf/renderer";
import {
  displayMunicNameAtom,
  firmSizeRangeAtom,
  imageResponseAtom,
  selectedSpatialLayerAtom,
  smallBusinessSectorAtom,
  smallBusinessSectorTrendsDataAtom,
  industriesDataAtom,
} from "../../shared/state/atoms";
import { Box, CircularProgress, Typography } from "@mui/material";
import styles from "../styles/PdfStyles";

// Wage distribution colour swatch (same as Establishments & Jobs)
const SEGMENT_COLORS = ['#009640', '#66bb6a', '#84746C', '#F5C242', '#F0A400'];
const SEGMENT_LABELS = ["Micro", "Small", "Medium", "Large", "V Large"];
// Short chart title (not the full dropdown label "Small Business Growth by Sector")
const DASHBOARD_TITLE = "Growth by Sector";

function SmallBusinessSectorTrendsPdf() {
  const [displayMunicname] = useAtom(displayMunicNameAtom);
  const [selectedSpatialLayer] = useAtom(selectedSpatialLayerAtom);
  const [imageResponse] = useAtom(imageResponseAtom);
  const [firmSizeRange] = useAtom(firmSizeRangeAtom);
  const [sic7] = useAtom(smallBusinessSectorAtom);
  const [industries] = useAtom(industriesDataAtom);
  const [rawData] = useAtom(smallBusinessSectorTrendsDataAtom);

  const [pageImages, setPageImages] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [processingStatus, setProcessingStatus] = useState("Initializing...");
  const [progress, setProgress] = useState(0);

  const pageRef0 = useRef();
  const pageRef1 = useRef();
  const pageRef2 = useRef();
  const pageRef3 = useRef();
  const pageRef4 = useRef();
  const pageRefs = useMemo(() => [pageRef0, pageRef1, pageRef2, pageRef3, pageRef4], []);

  const handles = firmSizeRange?.smallBusinessSectorTrends?.handles ?? [10, 50, 250, 1000];

  // Legend items with ranges (matching Establishments vs Employment style)
  const legendItems = [
    { segment: "Micro", label: `0\u2013${handles[0] - 1}` },
    { segment: "Small", label: `${handles[0]}\u2013${handles[1] - 1}` },
    { segment: "Medium", label: `${handles[1]}\u2013${handles[2] - 1}` },
    { segment: "Large", label: `${handles[2]}\u2013${handles[3] - 1}` },
    { segment: "V Large", label: `${handles[3]}+` },
  ];

  // Industry label
  const industryLabel = (() => {
    const ind = industries?.find((i) => String(i.code) === String(sic7));
    if (ind) return ind.activity;
    if (rawData?.[0]?.graph_label) return rawData[0].graph_label;
    return `Sector ${Math.abs(Number(sic7))}`;
  })();

  // Transform raw API data into chart-ready format
  const allChartData = useMemo(() => {
    if (!rawData || rawData.length === 0) return [];

    return rawData.map((graphItem) => {
      const lineDetails = graphItem.line_details?.[0];
      if (!lineDetails) return null;

      const chartData = [];
      Object.values(lineDetails).forEach((lines) => {
        lines.forEach((line) => {
          if (line.label && line.label.trim() !== "") {
            line.data.forEach(({ year, value }) => {
              let yearData = chartData.find((d) => d.year === year);
              if (!yearData) {
                yearData = { year };
                chartData.push(yearData);
              }
              yearData[line.label] = value;
            });
          }
        });
      });

      return {
        graph_label: graphItem.graph_label,
        y_axis_label: graphItem.y_axis_label,
        data: chartData,
      };
    }).filter(Boolean);
  }, [rawData]);

  const mainChartData = allChartData[0] || null;
  const otherChartData = allChartData.slice(1);

  // Pagination — same as Growth by Sector: first page = main + 3 small; additional pages = 9 each
  const firstPageCapacity = 3;
  const additionalPageCapacity = 9;
  const totalIndustries = otherChartData.length;
  const remainingIndustries = Math.max(0, totalIndustries - firstPageCapacity);
  const additionalPagesNeeded = Math.ceil(remainingIndustries / additionalPageCapacity);
  const totalPages = 1 + Math.min(4, additionalPagesNeeded);

  const getPageContent = (pageIndex) => {
    if (pageIndex === 0) {
      return otherChartData.slice(0, firstPageCapacity);
    }
    const startIdx = firstPageCapacity + (pageIndex - 1) * additionalPageCapacity;
    return otherChartData.slice(startIdx, startIdx + additionalPageCapacity);
  };

  // Sequential page processing with progress (mirrors Growth by Sector)
  useEffect(() => {
    const createFallbackImage = (pageIndex) => {
      try {
        const canvas = document.createElement("canvas");
        canvas.width = 800;
        canvas.height = 600;
        const ctx = canvas.getContext("2d");
        ctx.fillStyle = "#f8f9fa";
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.font = "16px Arial";
        ctx.fillStyle = "#212529";
        ctx.textAlign = "center";
        ctx.fillText("Chart visualization temporarily unavailable in PDF", canvas.width / 2, canvas.height / 2);
        return canvas.toDataURL("image/png");
      } catch (err) {
        return "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII=";
      }
    };

    const processPage = async (pageIndex) => {
      setProcessingStatus(`Processing page ${pageIndex + 1} of ${totalPages}...`);
      setProgress(Math.round((pageIndex / totalPages) * 100));

      try {
        if (!pageRefs[pageIndex]?.current) {
          return createFallbackImage(pageIndex);
        }

        const options = {
          cacheBust: true,
          quality: 1.0,
          pixelRatio: window.devicePixelRatio,
          filter: () => true,
        };

        const timeoutPromise = new Promise((_, reject) =>
          setTimeout(() => reject(new Error(`Timeout converting page ${pageIndex + 1}`)), 25000)
        );
        const conversionPromise = toPng(pageRefs[pageIndex].current, options);
        const dataUrl = await Promise.race([conversionPromise, timeoutPromise]);
        return dataUrl;
      } catch (err) {
        console.error(`Failed to convert Sector Trends page ${pageIndex + 1}:`, err);
        return createFallbackImage(pageIndex);
      }
    };

    const processAllPages = async () => {
      const newPageImages = [];
      for (let i = 0; i < totalPages; i++) {
        try {
          const imageUrl = await processPage(i);
          newPageImages.push(imageUrl);
          await new Promise((resolve) => setTimeout(resolve, 500));
        } catch (error) {
          newPageImages.push(createFallbackImage(i));
        }
      }
      setPageImages(newPageImages);
      setProcessingStatus("Complete");
      setProgress(100);
      setIsLoading(false);
    };

    const timer = setTimeout(() => processAllPages(), 1000);

    const fallbackTimer = setTimeout(() => {
      if (isLoading) {
        const fallbackImages = [];
        for (let i = 0; i < totalPages; i++) fallbackImages.push(createFallbackImage(i));
        setPageImages(fallbackImages);
        setProcessingStatus("Fallback Complete");
        setProgress(100);
        setIsLoading(false);
      }
    }, 90000);

    return () => {
      clearTimeout(timer);
      clearTimeout(fallbackTimer);
      setPageImages([]);
    };
  }, [totalPages, isLoading, pageRefs]);

  // Small chart component for sub-charts (mirrors Growth by Sector)
  const SmallChartComponent = ({ dataSlice }) => {
    const gridSize = 3;
    const placeholders = Array.from({ length: Math.max(0, gridSize - (dataSlice?.length || 0)) });

    return (
      <div style={{ display: "flex", flexWrap: "wrap", marginLeft: 105, marginTop: 20 }}>
        {dataSlice?.map((chart, index) => (
          <div key={`chart-${index}`} style={{ marginRight: 20, marginBottom: 20, width: 400 }}>
            <div style={{ height: 90, width: "100%" }}>
              {chart?.graph_label?.length >= 62 ? chart.graph_label.substring(0, 62) + " ..." : chart?.graph_label}
            </div>
            <AreaChart
              width={300}
              height={300}
              data={chart?.data}
              margin={{ top: 10, right: 10, left: 15, bottom: 10 }}
            >
              <XAxis dataKey="year" />
              <YAxis
                domain={[0, "auto"]}
                tickFormatter={(v) => (v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v)}
                label={{ value: "FTE Employees", angle: -90, dx: -17, fontSize: 12 }}
              />
              <CartesianGrid strokeDasharray="3" vertical={false} />
              {SEGMENT_LABELS.map((seg, i) => (
                <Area key={seg} type="linear" dataKey={seg} stackId="1" fill={SEGMENT_COLORS[i]} stroke={SEGMENT_COLORS[i]} fillOpacity={0.7} isAnimationActive={false} />
              ))}
            </AreaChart>
          </div>
        ))}
        {placeholders.map((_, index) => (
          <div key={`placeholder-${index}`} style={{ marginRight: 20, marginBottom: 20, width: 400 }} />
        ))}
      </div>
    );
  };

  // PDF legend — 5 items in a single row (matching Establishments & Jobs style)
  const LegendSection = () => (
    <View style={{ ...styles.legendSection, marginBottom: 10, marginTop: -10, flexDirection: "row", justifyContent: "center" }}>
      {legendItems.map((seg, i) => (
        <View key={seg.segment} style={{ ...styles.legendLine, marginRight: 12 }}>
          <View style={{ display: "flex", flexDirection: "row", alignItems: "center" }}>
            <View style={{ ...styles.legend, backgroundColor: SEGMENT_COLORS[i], width: 12, height: 12, marginRight: 4 }} />
            <Text style={{ ...styles.legendText, fontSize: 9 }}>{seg.segment}: {seg.label}</Text>
          </View>
        </View>
      ))}
    </View>
  );

  return (
    <>
      {/* Loading screen with progress (matches Growth by Sector) */}
      {isLoading ? (
        <div className="absolute left-0 top-0 w-full h-full opacity-30 bg-ccre-gray-900 z-20">
          <Box sx={{ textAlign: "center", position: "relative", top: "40%" }}>
            <CircularProgress color="success" size="3rem" />
            <Typography variant="h6" sx={{ mt: 2 }}>
              Generating {DASHBOARD_TITLE} PDF
            </Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>
              {processingStatus} ({progress}%)
            </Typography>
          </Box>
        </div>
      ) : pageImages.length > 0 ? (
        <PDFViewer style={{ flex: 1, width: "100%", height: "100vh" }}>
          <Document>
            {pageImages.map((imageUrl, index) => (
              <Page key={`page-${index}`} size="A4" style={styles.page}>
                <Image style={styles.headerLogo} src={`data:image/png;base64,'${imageResponse[13].base64_image}'`}></Image>
                <Text style={styles.headerText}>{selectedSpatialLayer?.name} | {displayMunicname}</Text>
                <View>
                  <Text style={{ ...styles.header, backgroundColor: "#1976d2" }}>Small Business</Text>
                </View>
                <View style={{ ...styles.section, paddingBottom: 0, marginBottom: 10 }}>
                  <Text style={{ ...styles.heading, marginBottom: 0 }}>{DASHBOARD_TITLE.toUpperCase()}</Text>
                </View>
                <View style={{ ...styles.section, paddingBottom: 0, marginBottom: 15 }}>
                  <Text style={styles.subheading}>
                    {industryLabel} | {index + 1}/{pageImages.length}
                  </Text>
                </View>
                {index === 0 && (
                  <View style={{ marginBottom: 10 }}>
                    <Text style={styles.paragraph}>Which sectors are driving small business employment trends in {displayMunicname}?</Text>
                    <Text style={styles.paragraph}>
                      This chart tracks FTE employment by industry and firm-size category over time. It shows which sectors are driving growth or decline in small business employment, and where the role of small businesses in the local economy is changing.
                    </Text>
                  </View>
                )}
                <View style={styles.section}>
                  <Image style={{ ...styles.image, marginTop: index === 0 ? 20 : 0, width: 550 }} src={imageUrl} />
                </View>
                <View style={styles.footerContainer}>
                  <Text style={styles.footer}>www.spatialtaxdata.org.za</Text>
                </View>
              </Page>
            ))}
          </Document>
        </PDFViewer>
      ) : null}

      {/* Hidden chart containers for conversion */}
      {/* Page 1 — Main chart + first 3 sub-charts */}
      <div style={{ position: "absolute", left: "-10000px" }}>
        <div ref={pageRefs[0]}>
          <div style={{ textAlign: "center", fontWeight: 600, fontSize: 22, marginBottom: 12, color: "#555" }}>
            {mainChartData?.graph_label}
          </div>
          {mainChartData && (
            <AreaChart width={1400} height={500} data={mainChartData.data} margin={{ top: 10, right: 50, left: 100, bottom: 50 }}>
              <XAxis dataKey="year" />
              <YAxis
                domain={[0, "auto"]}
                tickFormatter={(v) => (v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v)}
                label={{ value: "FTE Employees", angle: -90, dx: -50 }}
              />
              <CartesianGrid strokeDasharray="3" vertical={false} />
              {SEGMENT_LABELS.map((seg, i) => (
                <Area key={seg} type="linear" dataKey={seg} stackId="1" fill={SEGMENT_COLORS[i]} stroke={SEGMENT_COLORS[i]} fillOpacity={0.7} isAnimationActive={false} />
              ))}
            </AreaChart>
          )}
          <div style={{ marginTop: 15, marginBottom: 80, display: "flex", flexDirection: "row", gap: 40, justifyContent: "center" }}>
            {legendItems.map((seg, i) => (
              <div key={seg.segment} style={{ display: "flex", alignItems: "center" }}>
                <div style={{ width: 24, height: 24, marginRight: 10, backgroundColor: SEGMENT_COLORS[i] }} />
                <span style={{ fontSize: 24 }}>{seg.segment}: {seg.label}</span>
              </div>
            ))}
          </div>
          <SmallChartComponent dataSlice={getPageContent(0)} />
        </div>
      </div>

      {/* Additional pages — 9 sub-charts each */}
      {Array.from({ length: Math.min(4, additionalPagesNeeded) }).map((_, i) => {
        const pageIndex = i + 1;
        const pageContent = getPageContent(pageIndex);
        const firstRow = pageContent.slice(0, 3);
        const secondRow = pageContent.slice(3, 6);
        const thirdRow = pageContent.slice(6, 9);

        return (
          <div key={`page-container-${pageIndex}`} style={{ position: "absolute", left: "-10000px" }}>
            <div ref={pageRefs[pageIndex]}>
              {firstRow.length > 0 && <SmallChartComponent dataSlice={firstRow} />}
              {secondRow.length > 0 && <SmallChartComponent dataSlice={secondRow} />}
              {thirdRow.length > 0 && <SmallChartComponent dataSlice={thirdRow} />}
            </div>
          </div>
        );
      })}
    </>
  );
}

export default SmallBusinessSectorTrendsPdf;
